import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { NotificationToastComponent } from '../../shared/notification-toast/notification-toast.component';
import { FeatureTourComponent } from '../../shared/feature-tour/feature-tour.component';
import { HelpPanelComponent } from '../../shared/help-panel/help-panel.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { IdleTimeoutComponent } from '../../shared/idle-timeout/idle-timeout.component';
import { LayoutService } from '../../core/services/layout.service';
import { AuthService } from '../../core/auth/auth.service';
import { ThemeService } from '../../core/services/theme.service';
import { SettingsApiService } from '../../features/settings/settings-api.service';
import { ModuleTerminologyService } from '../../core/services/module-terminology.service';

@Component({
  selector: 'sw-app-shell',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, SidebarComponent, NotificationToastComponent, FeatureTourComponent, HelpPanelComponent, ConfirmDialogComponent, IdleTimeoutComponent],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss'
})
export class AppShellComponent implements OnInit {
  private readonly layout = inject(LayoutService);
  private readonly moduleTerms = inject(ModuleTerminologyService);
  private readonly auth = inject(AuthService);
  private readonly theme = inject(ThemeService);
  private readonly api = inject(SettingsApiService);
  readonly collapsed = this.layout.sidebarCollapsed;

  ngOnInit() {
    const tenantId = this.auth.currentUser()?.tenant_id;
    if (!tenantId) return;

    // Module naming ("Properties" -> "Units", etc.) is per tenant and read by the sidebar and every
    // module's chrome. Load it once here so the whole shell renders with the right wording.
    this.moduleTerms.ensureLoaded().catch(() => { /* naming is cosmetic; defaults are fine */ });

    // Paint the cached values immediately so there's no flash of the defaults while the server
    // round-trip resolves. These are shared by everyone signed into the tenant, so they're keyed
    // by tenant, not by user. Sidebar bg is applied before the brand color so, if there's no
    // override, applyColor()'s derived tint is what ends up showing (order doesn't matter when an
    // override IS set, since applySidebarBgColor always wins as the last direct write to the var).
    const cachedSidebarBg = this.theme.cachedSidebarBgForTenant(tenantId);
    if (cachedSidebarBg) this.theme.applySidebarBgColor(cachedSidebarBg);
    const cachedColor = this.theme.cachedForTenant(tenantId);
    if (cachedColor) this.theme.applyColor(cachedColor);
    const cachedSidebarText = this.theme.cachedSidebarTextForTenant(tenantId);
    if (cachedSidebarText) this.theme.applySidebarTextColor(cachedSidebarText);

    this.api.getOrganization().then(org => {
      // Only sync FROM the server when it actually has a value. Absence here is ambiguous —
      // could mean "tenant never set one", but could also mean the fetch failed — never treat
      // that as "explicitly cleared" and wipe the cache we just painted from.
      if (org.theme_color && org.theme_color !== cachedColor) {
        this.theme.applyColor(org.theme_color);
        this.theme.cacheForTenant(tenantId, org.theme_color);
      }
      if (org.sidebar_color && org.sidebar_color !== cachedSidebarBg) {
        this.theme.applySidebarBgColor(org.sidebar_color);
        this.theme.cacheSidebarBgForTenant(tenantId, org.sidebar_color);
      }
      if (org.sidebar_text_color && org.sidebar_text_color !== cachedSidebarText) {
        this.theme.applySidebarTextColor(org.sidebar_text_color);
        this.theme.cacheSidebarTextForTenant(tenantId, org.sidebar_text_color);
      }
    }).catch(() => {});
  }
}
